import { useEffect, useState } from 'react';
import { fetchPublishedStories, type ClientStory } from '../services/contentService';
import { CheckCircle2, Award, TrendingUp } from 'lucide-react';

export function ClientStoriesSection() {
  const [stories, setStories] = useState<ClientStory[]>([]);

  useEffect(() => {
    fetchPublishedStories().then(setStories);
  }, []);

  if (stories.length === 0) return null;

  return (
    <section id="results" className="light-section" style={{ padding: '80px 0', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
      <div className="container">
        <div style={{ textAlign: 'center', maxWidth: '640px', margin: '0 auto 48px auto' }}>
          <p className="eyebrow" style={{ color: 'var(--brand-accent)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.8rem', marginBottom: '8px' }}>
            VERIFIED PROOF &amp; OUTCOMES
          </p>
          <h2 style={{ fontSize: '2.2rem', fontWeight: 900, color: 'var(--navy)', letterSpacing: '-0.02em', margin: '0 0 12px 0' }}>
            Real Clients. <em>Measured Breakthroughs.</em>
          </h2>
          <p style={{ color: '#64748b', fontSize: '1rem', lineHeight: 1.6, margin: 0 }}>
            No fake promises or quick fixes. Here is how structured progressive overload and accountable nutrition transform busy lives.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px' }}>
          {stories.map((s) => (
            <div
              key={s.id}
              style={{
                background: '#ffffff',
                border: '1.5px solid #e2e8f0',
                borderRadius: '16px',
                padding: '28px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                boxShadow: '0 4px 12px rgba(15, 23, 42, 0.04)',
              }}
            >
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                  <span
                    style={{
                      background: '#eff6ff',
                      color: '#1d4ed8',
                      padding: '4px 10px',
                      borderRadius: '20px',
                      fontSize: '0.75rem',
                      fontWeight: 800,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <Award size={13} />
                    {s.transformationGoal}
                  </span>
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#16a34a', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    <CheckCircle2 size={13} /> Verified Consent
                  </span>
                </div>

                <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--navy)', margin: '0 0 12px 0', lineHeight: 1.35 }}>
                  "{s.headline}"
                </h3>

                <p style={{ fontSize: '0.9rem', color: '#475569', fontStyle: 'italic', lineHeight: 1.6, margin: '0 0 18px 0' }}>
                  "{s.storyQuote}"
                </p>
              </div>

              <div>
                <div
                  style={{
                    background: '#f1f5f9',
                    border: '1px solid #cbd5e1',
                    borderRadius: '10px',
                    padding: '10px 14px',
                    fontSize: '0.82rem',
                    color: '#1e293b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '16px',
                  }}
                >
                  <TrendingUp size={16} color="#0284c7" />
                  <span><strong>Results:</strong> {s.detailedResults}</span>
                </div>

                <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontWeight: 800, color: 'var(--navy)', fontSize: '0.95rem' }}>
                    {s.clientName}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                    {s.durationMonths} Months Coaching
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
